import { Injectable } from '@angular/core';
import type { TuiLanguageName } from '@taiga-ui/i18n/types';

interface MyMemoryResponse {
  responseData?: {
    translatedText?: string;
  };
  responseStatus?: number | string;
  quotaFinished?: boolean;
}

const LANGUAGE_CODES: Record<string, string> = {
  arabic: 'ar',
  belarusian: 'be',
  bengali: 'bn',
  chinese: 'zh-CN',
  dutch: 'nl',
  french: 'fr',
  german: 'de',
  greek: 'el',
  hebrew: 'he',
  hindi: 'hi',
  italian: 'it',
  japan: 'ja',
  japanese: 'ja',
  kazakh: 'kk',
  korean: 'ko',
  lithuanian: 'lt',
  malay: 'ms',
  polish: 'pl',
  portuguese: 'pt',
  russian: 'ru',
  spanish: 'es',
  thai: 'th',
  turkish: 'tr',
  ukrainian: 'uk',
  vietnamese: 'vi',
};

const MAX_CONCURRENT_REQUESTS = 4;
// MyMemory rejects queries longer than 500 bytes.
const MAX_QUERY_LENGTH = 500;
const STORAGE_PREFIX = 'runtime-translation:';
const PERSIST_DELAY_MS = 1000;

/**
 * Uses plain `fetch` instead of HttpClient so the auth interceptor never attaches
 * the user's token or org/parking headers to this third-party request.
 */
@Injectable({ providedIn: 'root' })
export class RuntimeTranslationService {
  private readonly stores = new Map<string, Record<string, string>>();
  private readonly pending = new Map<string, Promise<string>>();
  private readonly queue: Array<() => void> = [];
  private readonly dirtyLanguages = new Set<string>();
  private active = 0;
  private quotaExhausted = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;

  public supports(language: TuiLanguageName): boolean {
    return language !== 'english' && !!LANGUAGE_CODES[language.toLowerCase()];
  }

  public translate(text: string, language: TuiLanguageName): Promise<string> {
    const code = LANGUAGE_CODES[language.toLowerCase()];
    const normalized = text.trim();
    if (language === 'english' || !code || !normalized || normalized.length > MAX_QUERY_LENGTH) {
      return Promise.resolve(text);
    }

    const store = this.getStore(language);
    const stored = store[normalized];
    if (stored) {
      return Promise.resolve(stored);
    }

    if (this.quotaExhausted) {
      return Promise.resolve(text);
    }

    const key = `${language}:${normalized}`;
    const existing = this.pending.get(key);
    if (existing) {
      return existing;
    }

    const request = this.enqueue(() => this.requestTranslation(normalized, code))
      .then((translated) => {
        if (!translated) return text;
        store[normalized] = translated;
        this.schedulePersist(language);
        return translated;
      })
      .catch(() => text)
      .finally(() => this.pending.delete(key));

    this.pending.set(key, request);
    return request;
  }

  public clearCache(): void {
    for (const language of this.stores.keys()) {
      localStorage.removeItem(STORAGE_PREFIX + language);
    }
    this.stores.clear();
    this.pending.clear();
  }

  private async requestTranslation(text: string, code: string): Promise<string | null> {
    const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${code}&de=ranjan@sistem.com`;
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as MyMemoryResponse;
    const status = Number(body.responseStatus);
    if (body.quotaFinished || status === 429) {
      if (!this.quotaExhausted) {
        console.warn('[RuntimeTranslation] MyMemory daily quota reached; untranslated text will stay in English.');
      }
      this.quotaExhausted = true;
      return null;
    }
    if (status !== 200) {
      return null;
    }

    const translated = body.responseData?.translatedText?.trim();
    return translated ? decodeHtmlEntities(translated) : null;
  }

  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        this.active++;
        task()
          .then(resolve, reject)
          .finally(() => {
            this.active--;
            this.queue.shift()?.();
          });
      };

      if (this.active < MAX_CONCURRENT_REQUESTS) {
        run();
      } else {
        this.queue.push(run);
      }
    });
  }

  private getStore(language: string): Record<string, string> {
    let store = this.stores.get(language);
    if (!store) {
      try {
        store = JSON.parse(localStorage.getItem(STORAGE_PREFIX + language) || '{}') as Record<string, string>;
      } catch {
        store = {};
      }
      this.stores.set(language, store);
    }
    return store;
  }

  private schedulePersist(language: string): void {
    this.dirtyLanguages.add(language);
    if (this.persistTimer) return;

    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      for (const dirty of this.dirtyLanguages) {
        try {
          localStorage.setItem(STORAGE_PREFIX + dirty, JSON.stringify(this.stores.get(dirty) || {}));
        } catch {
          // Storage full: translations still work from memory for this session.
        }
      }
      this.dirtyLanguages.clear();
    }, PERSIST_DELAY_MS);
  }
}

function decodeHtmlEntities(value: string): string {
  if (!value.includes('&')) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}
